import { LockOutlined, LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Drawer, Form, Input, Layout, Menu, Modal, notification } from 'antd';
import axios from 'axios';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import AppMenu from './components/AppMenu';
import { AuthContext } from './AuthContext';
import AgentForm from './components/AgentForm';
import AgentList from './components/AgentList';
import TestCaseForm from './components/TestCaseForm';
import TestCaseList from './components/TestCaseList';


const { Sider, Content } = Layout;

const LLMs = {
  openAI: [
    { label: 'gpt-3.5-turbo', value: 'gpt-3.5-turbo' },
    { label: 'gpt-4-1106-preview', value: 'gpt-4-1106-preview' },
    { label: 'gpt-4-0125-preview', value: 'gpt-4-0125-preview' }
  ],
  cohere: [
    { label: 'command', value: 'command' },
    { label: 'command-light', value: 'command-light' },
    { label: 'command-light-nightly', value: 'command-light-nightly' },
    { label: 'command-r', value: 'command-r' },
    { label: 'command-r-plus', value: 'command-r-plus' },
  ],
  claude: [
    { label: 'claude-3-sonnet', value: "anthropic.claude-3-sonnet-20240229-v1:0" },
    { label: 'claude-3-5-sonnet', value: "anthropic.claude-3-5-sonnet-20240620-v1:0" },
    { label: 'claude-3-haiku', value: "anthropic.claude-3-haiku-20240307-v1:0" },
  ],
  mistral: [
    { label: 'mistral-7b-instruct', value: "mistral.mistral-7b-instruct-v0:2" },
    { label: "mistral-8x7b-instruct", value: "mistral.mixtral-8x7b-instruct-v0:1" },
    { label: "mistral-large", value: "mistral.mistral-large-2402-v1:0" },
  ],
  lama: [
    { label: 'llama3-8b-instruct', value: "meta.llama3-8b-instruct-v1:0" },
    { label: "llama3-70b-instruct", value: "meta.llama3-70b-instruct-v1:0" }
  ]
};

const App = () => {
  const { setAuthToken, userData, orgData, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('viewAgents');
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const handleLogout = async () => {
    // Use the logout function from AuthContext which calls the backend
    await logout();
  };

  const showLogoutModal = () => {
    setIsLogoutModalVisible(true);
  };

  const handleLogoutModalCancel = () => {
    setIsLogoutModalVisible(false);
  };

  const showDrawer = () => {
    setIsDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
  };

  const handlePasswordChange = async (values) => {
    try {
      const config = {
        method: "POST",
        url: `${process.env.REACT_APP_API}/api/auth/change-password`,
        data: {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        },
      };

      await axios(config);

      notification.success({
        message: 'Password Updated Successfully',
      });
      setIsDrawerVisible(false);
    } catch (error) {
      notification.error({
        message: 'Password Update Failed',
        description: error.message,
      });
    }


  };

  const menuItems = [
    // {
    //   key: 'agents',
    //   icon: <UserOutlined />,
    //   label: 'Agents',
    //   children: [
    //     { key: 'viewAgents', label: 'View Agents', onClick: () => setActiveView('viewAgents') },
    //     { key: 'createAgent', label: 'Create Agent', onClick: () => setActiveView('createAgent') }
    //   ],
    // },
    // {
    //   key: 'test-cases',
    //   icon: <FileTextOutlined />,
    //   label: 'Test Cases',
    //   children: [
    //     { key: 'viewTestCases', label: 'View Test Cases', onClick: () => setActiveView('viewTestCases') },
    //     { key: 'createTestCase', label: 'Create Test Case', onClick: () => setActiveView('createTestCase') }
    //   ],
    // },
    // Conditionally add the Admin Panel menu item based on the user role
    ...(orgData?.role?.toLowerCase() === 'admin' || orgData?.role?.toLowerCase() === 'superadmin' ? [
      {
        key: 'superAdmin',
        icon: <UserOutlined />,
        label: 'Admin Panel',
        onClick: () => navigate("/admin")
      }
    ] : []),
    {
      key: 'Account Setting',
      icon: <SettingOutlined />,
      label: 'Account Setting',
      onClick: () => showLogoutModal()
    }
  ];

  const renderContent = () => {
    switch (activeView) {
      case 'viewAgents':
        return <AgentList userData={userData} />;
      case 'createAgent':
        return <AgentForm LLMs={LLMs} />;
      case 'viewTestCases':
        return <TestCaseList userDetails={userData} />;
      case 'createTestCase':
        return <TestCaseForm LLMs={LLMs} />;
      default:
        return null;
    }
  };

  // Note: withCredentials is set globally in AuthContext.js
  // Authorization header is no longer needed - cookies are sent automatically
  
  // Setup Axios response interceptor for handling 401 errors
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;  
      // Check if the error status is 401 and the request is not already a retry
      if (error.response && error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true; // Mark the request as a retry
  
        try {
          await refreshAccessToken();
          return axios(originalRequest); // Retry the original request (cookie will be updated)
        } catch (refreshError) {
          console.error('Refresh token failed, redirecting to login:', refreshError);
          navigate('/login');
          return Promise.reject(refreshError);
        }
      }
  
      // If the response error is not 401 or the retry fails
      notification.error({
        message: 'Request Failed',
        description: error.response ? error.response.data.message : 'An unknown error occurred.',
      });
      return Promise.reject(error);
    }
  );
  
  
  const refreshAccessToken = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/api/auth/refresh-token`,
        {}
      );
      // Token is updated in HttpOnly cookie by the server
      setAuthToken(true);
    } catch (error) {
      console.error("Error refreshing access token:", error);
      // Redirect to login if refresh token fails
      navigate("/login");
      throw error;
    }
  };


  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#1a1a1a' }}>
     <AppMenu/>

      <Layout style={{ marginLeft: 300 }}>
        <Content
          style={{
            padding: '20px',
            height: '100vh',
            overflowY: 'auto',
          }}
        >
          {renderContent()}
        </Content>
      </Layout>

      {/* Logout and Change Password Modal */}
      <Modal
        title="Account Options"
        visible={isLogoutModalVisible}
        onCancel={handleLogoutModalCancel}
        footer={[
          <Button key="changePassword" icon={<LockOutlined />} onClick={() => { handleLogoutModalCancel(); showDrawer(); }}>
            Change Password
          </Button>,
          <Button key="logout" type="primary" icon={<LogoutOutlined />} onClick={handleLogout}>
            Logout
          </Button>,
        ]}
      >
        <p>What would you like to do?</p>
      </Modal>

      {/* Change Password Drawer */}
      <Drawer
        title="Change Password"
        placement="right"
        onClose={handleDrawerClose}
        visible={isDrawerVisible}
      >
        <Form layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item
            name="oldPassword"
            label="Old Password"
            rules={[{ required: true, message: 'Please enter your old password!' }]}
          >
            <Input.Password placeholder="Enter old password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[{ required: true, message: 'Please enter your new password!' }]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Re-enter New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Re-enter new password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Layout>
  );
};

export default App;
